// ============================================================
// MagnetixsLanding.jsx — LAYOUT (Struktur, liest nur aus content.js)
// Tailwind CSS. Icons: lucide-react.
// PREVIEW-Redesign nach Claude-Vorlage - Logik an bestehenden Wizard angebunden.
// ============================================================
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Truck, Image as ImageIcon, Check, ArrowRight, Plus, Menu, X } from "lucide-react";
import {
  heroContent, heroMagnets, trustBar, packages, packageFeatures, paymentNote, bulkCta,
  steps, qualityIntro, layerLabels, trueScaleImage,
  faqItems, contactEmail, footerLinks, inspirationItems,
} from "./content";

const ICONS = { shield: Shield, truck: Truck, image: ImageIcon, check: Check };
const LOGO_URL = "/assets/magnetixs-buendig-final.webp";

// Browser können die reale Bildschirm-DPI nicht auslesen, daher zeigen wir die 78x53mm Fläche
// mit der CSS-Standard-Dichte (96px/Zoll) - eine gute, konsistente Näherung für die Illustration.
const PX_PER_MM = 96 / 25.4;

const TrueScaleSection = ({ image }) => {
  const maxWidthPx = 78 * PX_PER_MM;

  return (
    <div className="mt-20 lg:mt-14 text-center px-4" data-testid="mtx-true-scale-section">
      <span className="text-[#E4231F] text-sm font-bold uppercase tracking-wider">Originalgröße</span>

      <div
        className="flex items-start justify-center gap-3 sm:gap-5 mt-10 lg:mt-6"
        style={{ "--mw": `clamp(140px, 55vw, ${maxWidthPx}px)` }}
      >
        <div className="flex flex-col items-center">
          <div
            className="rounded-[2px] overflow-hidden"
            style={{ width: "var(--mw)", aspectRatio: "78 / 53" }}
            data-testid="mtx-true-scale-magnet"
          >
            <img src={image} alt="Magnet Originalgröße" loading="lazy" className="w-full h-full object-cover" />
          </div>
          {/* Bemaßung Breite */}
          <div className="flex items-center mt-4" style={{ width: "var(--mw)" }} data-testid="mtx-dim-width">
            <span className="w-px h-2.5 bg-[#E4231F]" />
            <span className="flex-1 h-px bg-[#E4231F]" />
            <span className="w-px h-2.5 bg-[#E4231F]" />
          </div>
          <span className="text-[#E4231F] text-xs font-semibold tracking-wide mt-1.5">78 mm</span>
        </div>

        {/* Bemaßung Höhe */}
        <div className="flex items-center gap-2" style={{ height: "calc(var(--mw) * 53 / 78)" }} data-testid="mtx-dim-height">
          <div className="flex flex-col items-center h-full">
            <span className="h-px w-2.5 bg-[#E4231F]" />
            <span className="flex-1 w-px bg-[#E4231F]" />
            <span className="h-px w-2.5 bg-[#E4231F]" />
          </div>
          <span
            className="text-[#E4231F] text-xs font-semibold tracking-wide"
            style={{ writingMode: "vertical-rl" }}
          >
            53 mm
          </span>
        </div>
      </div>
    </div>
  );
};

export const MagnetixsLanding = ({ onStart, onAdminClick, heroDescription }) => {
  const [openFaq, setOpenFaq] = useState(0);
  const [openInspiration, setOpenInspiration] = useState(-1);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F3F0] font-sans">
      {/* ---------- HEADER ---------- */}
      <header className="bg-[#0A0A0A] border-b border-white/10 px-6 sm:px-10" data-testid="mtx-header">
        <div className="max-w-7xl lg:max-w-6xl mx-auto py-5 sm:py-7 flex items-center justify-between gap-4">
          <img
            src={LOGO_URL}
            alt="Magnetixs Logo - Personalisierte Fotomagnete selbst gestalten"
            className="h-14 sm:h-14 object-contain ml-1"
            data-testid="mtx-logo"
          />
          <nav className="hidden sm:flex gap-10 lg:gap-8">
            <a href="#preise" className="text-lg font-bold hover:text-[#ff4a45]" data-testid="mtx-nav-preise">Preise</a>
            <a href="#ablauf" className="text-lg font-bold hover:text-[#ff4a45]" data-testid="mtx-nav-ablauf">Ablauf</a>
            <a href="#faq" className="text-lg font-bold hover:text-[#ff4a45]" data-testid="mtx-nav-faq">FAQ</a>
          </nav>
          <button
            className="sm:hidden text-white p-1.5 -mr-1.5"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={mobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
            data-testid="mtx-mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="sm:hidden flex flex-col px-6 pb-3 border-t border-white/10" data-testid="mtx-mobile-menu">
            <a href="#preise" onClick={() => setMobileMenuOpen(false)} className="py-3 text-base font-bold border-b border-white/[0.06]" data-testid="mtx-mobile-nav-preise">Preise</a>
            <a href="#ablauf" onClick={() => setMobileMenuOpen(false)} className="py-3 text-base font-bold border-b border-white/[0.06]" data-testid="mtx-mobile-nav-ablauf">Ablauf</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="py-3 text-base font-bold" data-testid="mtx-mobile-nav-faq">FAQ</a>
          </nav>
        )}
      </header>

      {/* ---------- HERO ---------- */}
      <section className="px-6 sm:px-10 pt-8 sm:pt-10 lg:pt-3 pb-16 sm:pb-20 lg:pb-4" data-testid="mtx-hero-section">
        <div className="max-w-7xl lg:max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-block bg-[#E4231F] text-white text-sm font-bold uppercase tracking-wider px-4 py-2 rounded mb-7">
              {heroContent.badge}
            </span>
            <h1 lang="de" className="font-[Michroma] font-normal text-2xl sm:text-3xl md:text-4xl leading-[1.25] tracking-tight uppercase mb-7 break-words hyphens-none text-left">
              <span className="sr-only">Premium Kühlschrankmagnete – </span>
              <span>{heroContent.titleLine1}</span>
              <br />
              <span>{heroContent.titleLine2}</span>
              <br />
              <span className="text-[#E4231F]">{heroContent.titleLine3}</span>
            </h1>
            <p className="text-xl leading-relaxed text-[#B0B0AC] mb-8 max-w-md">
              {heroDescription || heroContent.description}
            </p>
            <button
              className="bg-[#E4231F] text-white rounded-md px-9 py-5 text-lg font-bold inline-flex items-center gap-2.5"
              onClick={() => onStart()}
              data-testid="mtx-hero-start-btn"
            >
              {heroContent.ctaLabel}
              <ArrowRight size={18} />
            </button>
            <p className="text-base text-[#7A7A76] mt-5">{heroContent.note}</p>
          </div>

          <div className="grid grid-cols-2 gap-8 p-2.5 lg:p-0" data-testid="mtx-hero-magnet-grid">
            {heroMagnets.map((m, i) => (
              <div
                key={`${m.id}-${i}`}
                className="aspect-[78/53] rounded-lg overflow-hidden border border-black/10 shadow-[0_16px_28px_-8px_rgba(0,0,0,0.45)]"
                style={{ transform: `rotate(${m.rotate})` }}
              >
                <img src={m.src} alt={m.alt} className="w-full h-full object-cover" />
              </div>
            ))}
            <p className="col-span-2 text-center text-[#8A8A8F] text-sm font-semibold">
              {heroContent.formatNote}
            </p>
          </div>
        </div>
      </section>

      {/* ---------- TRUST BAR ---------- */}
      <div className="bg-[#141416] border-y border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-5 sm:px-10 py-5 flex flex-wrap justify-center gap-5">
          {trustBar.map((item, i) => {
            const Icon = ICONS[item.icon];
            return (
              <div key={i} className="flex items-center gap-2.5">
                <Icon size={19} className="text-[#E4231F]" />
                <span className="text-sm font-semibold text-[#E7E7E4]">{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ---------- PREISE ---------- */}
      <section id="preise" className="px-5 sm:px-10 py-20 sm:py-28 lg:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 lg:mb-8">
            <span className="text-[#E4231F] text-sm font-bold uppercase tracking-wider">Preise</span>
            <h2 lang="de" className="font-[Michroma] font-normal text-2xl sm:text-3xl md:text-4xl uppercase my-3 break-words hyphens-auto">Wähle dein Paket</h2>
            <p className="text-[#A3A3A0] text-lg max-w-md mx-auto">Faire Preise für unvergessliche Erinnerungen. Keine versteckten Kosten.</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6" data-testid="mtx-pricing-grid">
            {packages.map((pkg) => {
              const featured = !!pkg.badge;
              return (
                <div
                  key={pkg.size}
                  data-testid={`mtx-price-card-${pkg.size}`}
                  className={`relative rounded-3xl p-3 sm:p-5 md:p-6 lg:p-8 flex flex-col overflow-hidden ${
                    featured ? "bg-[#E4231F] pt-8 sm:pt-10 md:pt-12 lg:pt-14" : "bg-[#141416] border border-white/10"
                  }`}
                >
                  {featured && (
                    <div className="absolute top-0 right-0 whitespace-nowrap bg-[#E0A93B] text-[#0A0A0A] text-[9px] sm:text-[10px] md:text-xs font-extrabold px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 rounded-bl-2xl">
                      {pkg.badge}
                    </div>
                  )}
                  <span className={`text-xs sm:text-sm font-bold uppercase tracking-wider ${featured ? "text-white/80" : "text-[#8A8A8F]"}`}>{pkg.label}</span>
                  <h3 lang="de" className="text-base sm:text-lg md:text-base lg:text-lg xl:text-2xl font-extrabold mt-2 sm:mt-2.5 text-white break-words hyphens-auto">{pkg.size} Fotomagnete</h3>
                  <div className="mt-3 sm:mt-4"><span className="text-xl sm:text-3xl md:text-2xl lg:text-4xl font-extrabold text-white whitespace-nowrap">{pkg.price}</span></div>
                  <p className={`mt-1.5 sm:mt-2 text-sm sm:text-base ${featured ? "text-white/85" : "text-[#A3A3A8]"}`}>{pkg.per}</p>
                  <ul className="my-5 sm:my-7 flex flex-col gap-2.5 sm:gap-3.5 flex-1">
                    {packageFeatures(pkg.size).map((li, i) => (
                      <li key={i} className={`flex items-center gap-2 sm:gap-2.5 text-sm sm:text-base ${featured ? "text-white/90" : "text-[#C9C9CC]"}`}>
                        <Check size={16} className="text-[#E4231F] shrink-0" />
                        {li}
                      </li>
                    ))}
                  </ul>
                  <button
                    className={`w-full min-h-12 sm:min-h-14 h-auto py-2.5 sm:py-3 rounded-full text-sm sm:text-base md:text-lg font-bold ${
                      featured ? "bg-white text-[#E4231F]" : "border-2 border-white text-white"
                    }`}
                    onClick={() => onStart(pkg.size)}
                    data-testid={`mtx-select-${pkg.size}-btn`}
                  >
                    {pkg.size}er auswählen
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-[#A3A3A0] text-lg mt-9">{paymentNote}</p>

          <div className="text-center mt-7">
            <a
              href={`mailto:${contactEmail}`}
              className="inline-block text-white border-2 border-white rounded-full px-8 py-3.5 text-base font-bold no-underline"
              data-testid="mtx-bulk-cta"
            >
              {bulkCta}
            </a>
          </div>
        </div>
      </section>

      {/* ---------- ABLAUF ---------- */}
      <section id="ablauf" className="px-5 sm:px-10 py-20 sm:py-28 bg-[#141416]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#E4231F] text-sm font-bold uppercase tracking-wider">Ablauf</span>
            <h2 lang="de" className="font-[Michroma] font-normal text-2xl sm:text-3xl md:text-4xl uppercase my-3 break-words hyphens-auto">So einfach geht’s</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 max-w-4xl mx-auto">
            {steps.map((step) => (
              <div key={step.n}>
                <span
                  className="font-[Michroma] text-5xl block mb-5"
                  style={{ WebkitTextStroke: "2px #E4231F", color: "transparent" }}
                >
                  {step.n}
                </span>
                <h3 className="text-xl font-extrabold mb-2.5 uppercase">{step.title}</h3>
                <p className="text-[#B0B0AC] text-base leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- QUALITÄT ---------- */}
      <section className="px-5 sm:px-10 py-20 sm:py-28 lg:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 lg:mb-10">
            <span className="text-[#E4231F] text-sm font-bold uppercase tracking-wider">{qualityIntro.eyebrow}</span>
            <h2 lang="de" className="font-[Michroma] font-normal text-2xl sm:text-3xl md:text-4xl uppercase my-3 break-words hyphens-auto">{qualityIntro.title}</h2>
            <p className="text-[#B0B0AC] text-lg max-w-2xl mx-auto leading-relaxed">{qualityIntro.description}</p>
          </div>

          {/* Explosionszeichnung */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-12 items-center">
            <div className="relative w-full max-w-xs mx-auto aspect-[320/340]">
              <div className="absolute w-[81.25%] aspect-[78/53] left-[18.75%] top-[44.1%] rounded-md bg-gradient-to-br from-[#2f2f30] to-[#1a1a1b] border border-white/[0.12] shadow-[0_12px_24px_-8px_rgba(0,0,0,0.6)]" />
              <div className="absolute w-[81.25%] aspect-[78/53] left-[12.5%] top-[29.4%] rounded-md bg-gradient-to-br from-[#c7c9cc] to-[#8d9094] shadow-[0_12px_24px_-8px_rgba(0,0,0,0.6)]" />
              <div className="absolute w-[81.25%] aspect-[78/53] left-[6.25%] top-[14.7%] rounded-md bg-gradient-to-br from-[#7a6a55] to-[#4a3f33] shadow-[0_12px_24px_-8px_rgba(0,0,0,0.6)]" />
              <div className="absolute w-[81.25%] aspect-[78/53] left-0 top-0 rounded-md bg-white/[0.18] border border-white/35 shadow-[0_12px_24px_-8px_rgba(0,0,0,0.6)]" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {layerLabels.map((l, i) => (
                <div key={i} className="border-l-2 border-[#E4231F] pl-4">
                  <p className="font-bold text-lg text-white mb-1">{l.title}</p>
                  <p className="text-sm text-[#A3A3A0]">{l.text}</p>
                </div>
              ))}
            </div>
          </div>

          <TrueScaleSection image={trueScaleImage} />
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" className="px-5 sm:px-10 py-20 sm:py-28 lg:py-14 bg-[#141416]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-14 lg:mb-6">
            <span className="text-[#E4231F] text-sm font-bold uppercase tracking-wider">Häufige Fragen</span>
            <h2 lang="de" className="font-[Michroma] font-normal text-2xl sm:text-3xl md:text-4xl uppercase my-3 break-words hyphens-auto">Alles was du wissen musst</h2>
          </div>
          <div>
            {faqItems.map((faq, i) => (
              <div key={i} className="border-b border-white/10 py-6">
                <div
                  className="flex justify-between items-center gap-5 cursor-pointer"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`mtx-faq-question-${i}`}
                >
                  <span className="text-lg font-bold text-white min-w-0 break-words">{faq.question}</span>
                  <span
                    className="text-[#E4231F] shrink-0 w-6 h-6 flex items-center justify-center transition-transform"
                    style={{ transform: openFaq === i ? "rotate(45deg)" : "rotate(0deg)" }}
                  >
                    <Plus size={22} />
                  </span>
                </div>
                {openFaq === i && (
                  <p className="text-[#A3A3A0] text-base leading-relaxed mt-4" data-testid={`mtx-faq-answer-${i}`}>{faq.answer}</p>
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-[#A3A3A0] text-base mt-10">
            Deine Frage war nicht dabei? Schreib uns:{" "}
            <a href={`mailto:${contactEmail}`} className="text-[#E4231F] font-semibold">{contactEmail}</a>
          </p>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section className="px-5 sm:px-10 py-16 sm:py-24 lg:py-14 bg-[#E4231F] text-center">
        <div className="max-w-2xl mx-auto">
          <h2 lang="de" className="font-[Michroma] font-normal text-2xl sm:text-3xl md:text-4xl leading-[1.3] uppercase text-white mb-5 break-words hyphens-auto">
            Bereit für deine Fotomagnete?
          </h2>
          <p className="text-lg text-white/90 mb-10">Jetzt gestalten und in wenigen Tagen in der Hand halten.</p>
          <button
            className="bg-[#0A0A0A] text-white rounded-md px-9 py-5 text-lg font-bold inline-flex items-center gap-2.5"
            onClick={() => onStart()}
            data-testid="mtx-final-cta-btn"
          >
            Jetzt Fotomagnete gestalten
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* ---------- INSPIRATIONS-LEXIKON (SEO) ---------- */}
      <section className="px-5 sm:px-10 py-20 sm:py-28 lg:py-16 bg-[#0A0A0A] border-t border-white/[0.06]" data-testid="mtx-inspiration-section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-14 lg:mb-8">
            <span className="inline-block w-10 h-[2px] bg-[#E4231F] mb-4" />
            <h2 lang="de" className="font-[Michroma] font-normal text-xl sm:text-3xl md:text-4xl uppercase text-[#E4231F] leading-[1.3] break-words hyphens-auto">
              Kühlschrankmagnete für deine schönsten Momente
            </h2>
          </div>
          <div>
            {inspirationItems.map((item, i) => (
              <div key={i} className="border-b border-[#E4231F]/15 py-6">
                <button
                  type="button"
                  className="w-full flex justify-between items-center gap-5 cursor-pointer text-left"
                  onClick={() => setOpenInspiration(openInspiration === i ? -1 : i)}
                  data-testid={`mtx-inspiration-question-${i}`}
                >
                  <span className="text-lg font-bold text-white min-w-0 break-words">{item.title}</span>
                  <span
                    className="text-[#E4231F] shrink-0 w-6 h-6 flex items-center justify-center transition-transform"
                    style={{ transform: openInspiration === i ? "rotate(45deg)" : "rotate(0deg)" }}
                  >
                    <Plus size={22} />
                  </span>
                </button>
                {openInspiration === i && (
                  <div className="mt-4">
                    <p className="text-[#A3A3A0] text-base leading-relaxed break-words" data-testid={`mtx-inspiration-answer-${i}`}>{item.text}</p>
                    <Link
                      to={`/kuehlschrankmagnete/${item.slug}`}
                      className="inline-flex items-center gap-1.5 text-[#E4231F] font-semibold text-sm mt-3 hover:text-[#ff4a45]"
                      data-testid={`mtx-inspiration-link-${item.slug}`}
                    >
                      {item.title} entdecken <ArrowRight size={14} />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="bg-[#0A0A0A] border-t border-white/[0.08] px-5 sm:px-10 py-9 lg:py-6" data-testid="mtx-footer">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-6">
          <img src={LOGO_URL} alt="Magnetixs" className="h-8 object-contain" />
          <div className="flex flex-wrap gap-6 text-[#8A8A8F] text-sm">
            {footerLinks.map((link, i) => (
              <Link key={i} to={link.to} className="text-[#8A8A8F] no-underline hover:text-white" data-testid={`mtx-footer-link-${link.to.replace("/", "")}`}>{link.label}</Link>
            ))}
            <a href={`mailto:${contactEmail}`} className="text-[#8A8A8F] no-underline">Kontakt</a>
          </div>
          <p className="text-[#8A8A8F] text-sm">© 2025 Magnetixs</p>
        </div>
        <div className="max-w-7xl mx-auto mt-6 pt-6 border-t border-white/[0.06] flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#5A5A58]">
          {inspirationItems.map((item) => (
            <Link
              key={item.slug}
              to={`/kuehlschrankmagnete/${item.slug}`}
              className="text-[#5A5A58] no-underline hover:text-[#8A8A8F]"
              data-testid={`mtx-footer-topic-link-${item.slug}`}
            >
              Kühlschrankmagnete {item.title}
            </Link>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default MagnetixsLanding;
